import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useLocalCollection } from '@/hooks/useLocalCollection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { badgeVariants } from '@/components/ui/badge';
import { BottomSheet } from '@/components/mobile/BottomSheet';
import { HABITS_KEY, type Habit } from '@/lib/entry';
import { successFeedback, errorFeedback, selectionFeedback } from '@/lib/native';
import { TONES } from '@/brand/tones';
import { cn } from '@/lib/utils';

const SUGGESTIONS = ['Walk 20 minutes', 'Read before bed', 'No phone until 9am', 'Stretch', 'Write one page'];

/**
 * Creating a habit. Deliberately one decision (what) plus one optional one (which colour) — an
 * onboarding form with six fields is where habit apps lose people before they have a single streak.
 *
 * Saving confirms in three registers at once: a success haptic, a written confirmation, and the item
 * appearing on Today. Any one alone is missable.
 */
export function AddScreen() {
	const { add } = useLocalCollection<Habit>(HABITS_KEY);
	const navigate = useNavigate();
	const [name, setName] = useState('');
	const [tone, setTone] = useState(0);
	const [confirming, setConfirming] = useState<Habit | null>(null);
	const [error, setError] = useState<string | null>(null);

	const submit = (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = name.trim();
		if (!trimmed) {
			setError('Give the habit a name so you recognise it on the list.');
			void errorFeedback();
			return;
		}

		const habit: Habit = {
			id: crypto.randomUUID(),
			name: trimmed,
			tone,
			createdAt: Date.now(),
			done: [],
		};
		add(habit);
		void successFeedback();
		setName('');
		setError(null);
		setConfirming(habit);
	};

	return (
		<div className="flex flex-col px-5 pt-6">
			<h1 className="text-[28px] font-semibold leading-tight" style={{ fontFamily: 'var(--brand-display)' }}>
				New habit
			</h1>
			<p className="text-muted-foreground mt-0.5 text-sm">Small enough to do on your worst day.</p>

			<form onSubmit={submit} className="mt-6 flex flex-col gap-5">
				<div className="flex flex-col gap-2">
					<Label htmlFor="habit-name">What will you do?</Label>
					<Input
						id="habit-name"
						value={name}
						onChange={(e) => {
							setName(e.target.value);
							if (error) setError(null);
						}}
						placeholder="Walk 20 minutes"
						autoComplete="off"
						aria-invalid={Boolean(error)}
						aria-describedby={error ? 'habit-error' : undefined}
						/* text-base, not smaller: anything under 16px makes iOS zoom the page on focus.
						   min-h keeps it a comfortable phone target rather than the kit's desktop height. */
						className="min-h-[52px] text-base"
					/>
					{error && (
						<p id="habit-error" role="alert" className="text-destructive text-[13px]">
							{error}
						</p>
					)}
				</div>

				<div className="flex flex-col gap-2">
					<span className="text-[13px] font-medium">Colour</span>
					<div className="flex gap-2.5">
						{TONES.map((value, index) => (
							<button
								key={value}
								type="button"
								onClick={() => {
									setTone(index);
									void selectionFeedback();
								}}
								aria-label={`Colour ${index + 1}`}
								aria-pressed={tone === index}
								className="flex size-11 items-center justify-center rounded-full"
								style={{
									background: value,
									color: '#fff',
									outline: tone === index ? '2px solid var(--brand-ink)' : 'none',
									outlineOffset: 2,
								}}
							>
								{tone === index && <Check className="size-4" strokeWidth={3} />}
							</button>
						))}
					</div>
				</div>

				<div className="flex flex-col gap-2">
					<span className="text-[13px] font-medium">Or start from one of these</span>
					{/* Badge has no asChild in this kit, so its variants go on a real button — the documented
					    shadcn pattern, and it keeps the element semantically a control. */}
					<div className="flex flex-wrap gap-2">
						{SUGGESTIONS.map((suggestion) => (
							<button
								key={suggestion}
								type="button"
								onClick={() => {
									setName(suggestion);
									void selectionFeedback();
								}}
								className={cn(
									badgeVariants({ variant: 'secondary' }),
									'min-h-[38px] cursor-pointer rounded-full px-3.5 text-[13px] font-normal',
								)}
							>
								{suggestion}
							</button>
						))}
					</div>
				</div>

				<Button type="submit" size="lg" className="mt-1 min-h-[52px] rounded-full">
					Add habit
				</Button>
			</form>

			<BottomSheet
				open={Boolean(confirming)}
				onClose={() => setConfirming(null)}
				title="Habit added"
				description={confirming ? `"${confirming.name}" is on your list for today.` : undefined}
				footer={
					<div className="flex flex-col gap-2.5">
						<Button
							size="lg"
							className="min-h-[48px] w-full rounded-full"
							onClick={() => {
								setConfirming(null);
								void navigate({ to: '/' });
							}}
						>
							See today
						</Button>
						<Button variant="ghost" size="lg" className="min-h-[48px] w-full" onClick={() => setConfirming(null)}>
							Add another
						</Button>
					</div>
				}
			>
				<p className="text-muted-foreground text-sm">
					Your streak starts the first day you tick it off. Miss a day and it resets — the point is
					returning, not never failing.
				</p>
			</BottomSheet>
		</div>
	);
}
